import React from "react";
import { AnimatePresence, motion, MotionValue, useTransform } from "framer-motion";
import type { ImageTile } from "../../types/film";
const EASE = [0.23, 1, 0.32, 1] as const;
const TOTAL_SECONDS = 60;
interface ProjectorGateProps {
  frames: ImageTile[];
  step: number;
  progress: MotionValue<number>;
}
export function ProjectorGate({
  frames,
  step,
  progress
}: ProjectorGateProps) {
  const frame = frames[step];
  const timecode = useTransform(progress, p => {
    const total = Math.round(p * TOTAL_SECONDS * 24);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `00:${pad(Math.floor(total / 24 / 60))}:${pad(Math.floor(total / 24) % 60)}:${pad(total % 24)}`;
  });
  return <div className="flex h-full min-h-0 flex-col" data-id="element-319" data-mp="a9xw0rpduccr">
      {/* Gate */}
      <div className="projector-flicker relative min-h-0 w-full flex-1 overflow-hidden border border-hairline bg-black" data-id="element-320" data-mp="273iyhst2xfqd">
        <AnimatePresence initial={false} data-id="element-321" data-mp="2c1khyeuf3h2d">
          <motion.img key={frame.id} src={frame.image} alt={frame.alt} className="absolute inset-0 h-full w-full object-cover" initial={{
          opacity: 0,
          scale: 1.05
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: EASE
        }} data-id="element-322" data-mp="2c4gd5vevrrn4" />
        </AnimatePresence>
        <div className="grain pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-323" data-mp="27ezia68w4aeo" />
        <div className="pointer-events-none absolute inset-0" style={{
        background: "linear-gradient(0deg, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0) 65%)"
      }} aria-hidden="true" data-id="element-324" data-mp="346jx2nkpwgay" />

        {/* Gate corner marks */}
        {["left-3 top-3 border-l border-t", "right-3 top-3 border-r border-t", "left-3 bottom-3 border-l border-b", "right-3 bottom-3 border-r border-b"].map(pos => <span key={pos} className={`pointer-events-none absolute h-5 w-5 border-white/50 md:h-7 md:w-7 ${pos}`} aria-hidden="true" data-id="element-325" data-mp="1eseutgvjcige" />)}

        <div className="absolute inset-x-5 top-5 flex items-center justify-between font-mono text-[10px] tracking-[0.14em] md:inset-x-7 md:top-6 md:text-[11px]" data-id="element-326" data-mp="1gavj3b7i8exk">
          <span className="flex items-center gap-2 text-signal" data-id="element-327" data-mp="1795qw35stbyq">
            <span className="rec-dot is-round inline-block h-1.5 w-1.5 bg-signal" aria-hidden="true" data-id="element-328" data-mp="17b03mu6x1xrq" />
            REC
            <motion.span className="tabular-nums text-white/80" data-id="element-329" data-mp="xj2sfnfwr5t0">{timecode}</motion.span>
          </span>
          <span className="text-white/80" data-id="element-330" data-mp="129j535bwmneb">FR 0{step + 1} / 0{frames.length}</span>
        </div>

        <AnimatePresence mode="wait" initial={false} data-id="element-331" data-mp="oie9qffkkh9s">
          <motion.div key={frame.id} className="absolute inset-x-5 bottom-5 md:inset-x-7 md:bottom-7" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.25,
          ease: EASE
        }} data-id="element-332" data-mp="ojz9cjgqqqei">
            <h3 className="font-serif text-[24px] leading-none tracking-[-0.01em] text-white md:text-[32px] lg:text-[36px]" data-id="element-333" data-mp="jle9il5ei8ql">{frame.title}</h3>
            <p className="mt-2 max-w-[440px] text-[13px] leading-relaxed text-white/80 md:text-[14px]" data-id="element-334" data-mp="3r4m5d4215ygo">{frame.caption}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Film strip feeding the gate */}
      <div className="mt-3 bg-card px-3 py-2" data-id="element-335" data-mp="4vtbotve34v8">
        <Sprockets data-id="element-336" data-mp="iwdsjkhquqm3" />
        <ol className="my-1.5 hidden grid-cols-4 gap-2 md:grid" aria-label="Process steps" data-id="element-337" data-mp="ivvmjw5a4y0z">
          {frames.map((f, i) => <li key={f.id} aria-current={i === step ? "step" : undefined} className={`relative h-14 overflow-hidden border transition-[border-color,opacity] duration-200 lg:h-16 ${i === step ? "border-signal opacity-100" : "border-transparent opacity-40"}`} data-id="element-338" data-mp="dwj3znlfyd47" data-mpi={i}>
              <img src={f.image} alt="" className="h-full w-full object-cover" data-id="element-339" data-mp="xla0zinpq5n8" />
              <span className="absolute inset-x-0 bottom-0 bg-black/70 px-1.5 py-0.5 font-mono text-[9px] tracking-[0.12em] text-white" data-id="element-340" data-mp="xj71m80o705u">
                FR 0{i + 1} · {f.title.toUpperCase()}
              </span>
            </li>)}
        </ol>
        <div className="my-1.5 h-[2px] bg-white/10 md:my-0" aria-hidden="true" data-id="element-341" data-mp="30tk5bn1xgqn2">
          <motion.div className="h-full origin-left bg-signal" style={{
          scaleX: progress
        }} data-id="element-342" data-mp="35tet3hqep03w" />
        </div>
        <Sprockets data-id="element-343" data-mp="285oc4sbkhgv1" />
      </div>
    </div>;
}
function Sprockets() {
  return <div className="flex h-2.5 items-center justify-between overflow-hidden" aria-hidden="true" data-id="element-344" data-mp="2d3zi2ryqr77z">
      {Array.from({
      length: 48
    }).map((_, i) => <span key={i} className="mx-[3px] h-1.5 w-2.5 shrink-0 bg-ink" data-id="element-345" data-mp="2i1raro77mxd8" data-mpi={i} />)}
    </div>;
}